// Dependencies
import React from 'react';

// Hooks
import { useDispatch, useSelector } from 'src/store/store';

// Internal Components
import { IconButtonAnimate } from '../../../components/animate';
import Iconify from 'src/components/Iconify';
import { changeThemeMode } from 'src/store/slices/theme';

// ----------------------------------------------------------------------
type Props = {
  sideBarOpen: boolean;
};
/**
 * Light and dark mode toggle button in the navbar
 */
export default function ModePopover({ sideBarOpen }: Props) {
  // =={ Redux }===============================================================
  const currentMode = useSelector((state) => state.theme.mode);
  const dispatch = useDispatch();

  // =={ State }===============================================================
  const [changed, setChanged] = React.useState('');

  // =={ Ref }===============================================================
  const timeout = React.useRef<NodeJS.Timeout | null>(null);

  // =={ Functions }===============================================================
  const handleChange = (mode: 'auto' | 'light' | 'dark') => () => {
    dispatch(changeThemeMode(mode));
    setChanged(Math.random().toString(36).substring(7));
  };

  // =={ Effects }===============================================================
  React.useEffect(() => {
    if (changed) {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        setChanged('');
      }, 3000);
    }
    () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [changed]);

  // =={ Renders }===============================================================
  return (
    <IconButtonAnimate
      onClick={handleChange(
        currentMode === 'auto' ? 'light' : currentMode === 'light' ? 'dark' : 'auto'
      )}
      sx={{
        width: 40,
        height: 40,
        ...(!!changed && { bgcolor: 'action.selected' }),
      }}
    >
      {currentMode === 'auto' ? (
        <Iconify icon="material-symbols:night-sight-auto-rounded" width={24} height={24} />
      ) : currentMode === 'light' ? (
        <Iconify icon="material-symbols:light-mode-rounded" width={24} height={24} />
      ) : (
        <Iconify icon="iconamoon:mode-dark-fill" width={24} height={24} />
      )}
    </IconButtonAnimate>
  );
}
